import { Injectable } from '@angular/core';
import { NetworkingService } from '../networking.service';
import { catchError, finalize, map, Observable, of, switchMap, throwError } from 'rxjs';
import { LessonPageItem } from '../../data/models/LessonPageItem';
import { API } from '../../data/api';
import { LessonPageItemTypeEnum } from '../../data/enums/LessonPageItemType';
import { LessonPageItemTypeService } from './lesson-page-item-type.service';
import { environment } from '../../../environments/environment';
import { SharedDataService } from '../../helper/services/shared-data.service';

@Injectable({
  providedIn: 'root'
})
export class LessonPageItemService {
  constructor(private networkingService: NetworkingService, private sharedDataService: SharedDataService) {}

  getLessonPageItemList(id: string): Observable<LessonPageItem[]> {
    return this.networkingService.getRequest<LessonPageItem[]>(API.lessonPageItemList(id)).pipe(
      map((items: LessonPageItem[]) => {
        return items.map((item) => {
          if (item.type?.title === LessonPageItemTypeEnum.VIDEO) {
            item.videoData = `${environment.url}/${environment.backendVersion}/${API.lessonPageItemDownload(item.id)}`;
          }

          if (item.title === 'None') {
            item.title = null;
          }

          return item;
        });
      }),
      catchError(() => of([]))
    );
  }

  getLessonPageItemDetails(id: string): Observable<LessonPageItem> {
    const endpoint = API.lessonPageItemDetails(id);

    return this.networkingService.getRequest<LessonPageItem>(endpoint).pipe(
      map((item) => {
        if (item.type?.title === LessonPageItemTypeEnum.VIDEO) {
          item.videoData = `${environment.url}/${environment.backendVersion}/${API.lessonPageItemDownload(item.id)}`;
        }

        if (item.title === 'None') {
          item.title = null;
        }

        return item;
      }),
      catchError((error) => throwError(() => error))
    );
  }

  deleteLessonPageItem(id: string): Observable<any> {
    const endpoint = API.lessonPageItemDelete(id);
    return this.networkingService.deleteRequest(endpoint)
      .pipe(
        map(response => response)
      );
  }

  uploadLessonPageItem(
    file: File,
    lessonPageId: string,
    lessonPageItemTypeId: string,
    title?: string
  ): Observable<LessonPageItem> {
    const formData = new FormData();
  
    formData.append('video', file);
    formData.append('lesson_page_id', lessonPageId);
    formData.append('lesson_page_item_type_id', lessonPageItemTypeId);
    if (title) {
      formData.append('title', title);
    }
  
    const endpoint = API.lessonPageItemUpload;
  
    return this.networkingService.postRequest(endpoint, formData).pipe(
      map(response => response as LessonPageItem),
      catchError(error => throwError(() => error))
    );
  }

  createLessonPageItem(lessonItemData: {
    title?: string,
    content: string,
    lesson_page_id: string,
    lessonPageItemTypeId: string
  }): Observable<LessonPageItem> {
    const payload: any = {
      title: lessonItemData.title,
      lesson_page_id: lessonItemData.lesson_page_id,
      content: lessonItemData.content,
      lesson_page_item_type_id: lessonItemData.lessonPageItemTypeId
    };

    return this.networkingService.postRequest(API.lessonPageItemCreate, payload).pipe(
      map((response) => response as LessonPageItem),
      catchError((error) => {
        console.error('Failed to create lesson page item:', error);
        return throwError(() => new Error('Failed to create lesson page item'));
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff
      })
    )
  }

  alterLessonPageItem(
    id: string,
    lessonItemData: {
      title?: string;
      content?: string;
    }
  ): Observable<LessonPageItem> {
    const endpoint = API.lessonPageItemAlter(id);
  
    const payload: any = {
      ...(lessonItemData.title !== undefined ? { title: lessonItemData.title } : {}),
      ...(lessonItemData.content !== undefined ? { content: lessonItemData.content } : {}),
    };
  
    return this.networkingService.putRequest(endpoint, payload).pipe(
      map((response) => response as LessonPageItem),
      catchError((error) => {
        console.error('Failed to alter lesson page item:', error);
        return throwError(() => new Error('Failed to alter lesson page item'));
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }
}
